import { Component, inject, Input } from '@angular/core';
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PostGetProviderSearchRequest } from '../../../shared/model/providerlocationsearch/PostGetProviderSearchRequest';
import { LocationCoordinates } from '../../../shared/model/LocationCoordinates';
import { MCAppGatewayService } from '../../../shared/services/mcappgateway/mcappgateway.service';
import { CommonModule } from '@angular/common';
import { SearchStateService } from '../../shared/services/search-state.service';
import { MonitoringService } from '../../../shared/services/monitoring/monitoring-service';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { ProviderSearchFilters, SearchFilter } from '../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-provider-search',
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    MatButtonModule, 
    MatIconModule, 
    MatFormFieldModule, 
    MatInputModule, 
    MatAutocompleteModule,
    MatProgressSpinner
  ],
  templateUrl: './provider-search.component.html',
  styleUrl: './provider-search.component.css'
})
export class ProviderSearchComponent {
  mcAppGateway = inject(MCAppGatewayService);
  searchState = inject(SearchStateService);

  public title: string = 'Find a Provider';
  public searchByText: string = 'Search by name or specialty';
  public namePlaceholderText: string = 'Provider name or specialty';
  public cityZipPlaceholderText: string = 'City or zip code (required)*'
  public locationInformationRequiredText: string = 'Location information required*';
  public searchBtnText: string = 'Search';
  public quickSearchTitleText: string = 'Quick searches:';
  public showSpinner: boolean = false;

  public eptID: string = '';
  
  @Input() locale: string = '';
  @Input() currentLocation: LocationCoordinates | null = null;
  @Input() providerQuickSearchOptions: any[] | null = null;
  @Input() providerSearchFilters: ProviderSearchFilters | null = null;
  @Input() patientAffiliation: string | null = null;
  @Input() providerSearchTypeAheadOptions: string[] = [];
  @Input() cityTypeAheadOptions: string[] = [];

  providerSearchQuery$ = this.searchState.providerSearchQuery$;
  showSpinner$ = this.searchState.showSpinner$;
  filteredNameOrSpecialties$: Observable<string[]> = new Observable<string[]>();
  filteredCityOptions$: Observable<string[]> = new Observable<string[]>();

  sortMethod: "distance" | "availability" | "name" = "distance";
  providerSearchForm = new FormGroup({
    nameOrSpecialty: new FormControl(''),
    cityOrZip: new FormControl('', Validators.required)
  });


  constructor(private translate: TranslateService, private monitoringService: MonitoringService) {
    this.translate.use(sessionStorage.getItem('locale') || 'en-US'); // Default to 'en-US' if no locale is provided
  }

  ngOnInit() {
    this.translate.get('FINDAPROVIDER').subscribe((translated: string) => {
      this.title = translated;
    });

    this.translate.get('PROVIDER_SEARCH_BY').subscribe((translated: string) => {
      this.searchByText = translated;
    }); 

    this.translate.get('PROVIDER_NAME_PLACEHOLDER').subscribe((translated: string) => {
      this.namePlaceholderText = translated;
    });

    this.translate.get('PROVIDER_CITYZIP_PLACEHOLDER').subscribe((translated: string) => {
      this.cityZipPlaceholderText = translated;
    });

    this.translate.get('SEARCH').subscribe((translated: string) => {
      this.searchBtnText = translated;
    });

    this.translate.get('QUICK_SEARCH_TITLE').subscribe((translated: string) => {  
      this.quickSearchTitleText = translated;
    });

    this.translate.get('LOCATION_INFORMATION_REQUIRED').subscribe((translated: string) => {  
      this.locationInformationRequiredText = translated;
    });

    // You can perform additional initialization here if needed
    this.providerSearchQuery$.subscribe(query => {
      if (query) {
        this.providerSearchForm.patchValue({
          nameOrSpecialty: query.nameOrSpecialty,
          cityOrZip: query.cityOrZip
        });
      }
    }); 

    this.searchState.providerSortMethod$.subscribe( sortMethod => {
      if (sortMethod) {
        this.sortMethod = sortMethod
      }
    });
    
    this.showSpinner$.subscribe(show => {
      this.showSpinner = show;
    });

    // Setup autocomplete filtering for name/specialty
    this.filteredNameOrSpecialties$ = this.providerSearchForm.get('nameOrSpecialty')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterNameOrSpecialties(value || ''))
    );

    // Setup autocomplete filtering for city/zip
    this.filteredCityOptions$ = this.providerSearchForm.get('cityOrZip')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCityOptions(value || ''))
    );
  }

  private _filterNameOrSpecialties(value: string): string[] {
    // Only show suggestions if 3 or more characters have been typed
    if (!value || value.length < 3) {
      return [];
    }

    if (!this.providerSearchTypeAheadOptions || !Array.isArray(this.providerSearchTypeAheadOptions)) {
      console.log('No type ahead options available');
      return [];
    }

    // Remove special characters and convert to lowercase for comparison
    const normalizeString = (str: string) => str.toLowerCase().replace(/[^a-z0-9\s]/gi, '');
    const filterValue = normalizeString(value);
    
    const filtered = this.providerSearchTypeAheadOptions.filter(option => 
      normalizeString(option).includes(filterValue)
    );
    console.log('Filtered results:', filtered);
    return filtered;
  }

  private _filterCityOptions(value: string): string[] {
    // Only show suggestions if 3 or more characters have been typed
    if (!value || value.length < 3) {
      return [];
    }

    if (!this.cityTypeAheadOptions || !Array.isArray(this.cityTypeAheadOptions)) {
      console.log('No city type ahead options available');
      return [];
    }

    // Remove special characters and convert to lowercase for comparison
    const normalizeString = (str: string) => str.toLowerCase().replace(/[^a-z0-9\s]/gi, '');
    const filterValue = normalizeString(value);
    
    const filtered = this.cityTypeAheadOptions.filter(option => 
      normalizeString(option).includes(filterValue)
    );
    console.log('Filtered city options:', filtered);
    return filtered;
  }

  onSubmit() {
    this.searchState.setShowSpinner(true);

    if (this.providerSearchForm.valid) {
      const formValues = this.providerSearchForm.value;

      if (formValues.cityOrZip) {

        var request = new PostGetProviderSearchRequest();
        request.nameOrSpecialty = formValues.nameOrSpecialty || '';
        request.cityOrZip = formValues.cityOrZip;
        request.affiliations = this.patientAffiliation ?? '';
             
        if(this.currentLocation != null) {
          request.latitude = this.currentLocation?.latitude;
          request.longitude = this.currentLocation?.longitude;
        }

        sessionStorage.setItem('providerSearchMethod', 'freeText');
        console.log('Form Submitted!', formValues, request );
        this.postGetProviderSearch(request);
      }
      else {
        alert('Please fill in all required fields before searching.');
        this.searchState.setShowSpinner(false);
      }
    }
    else {
      alert('Form is invalid. Please check the input fields.');
      console.log('Form is invalid', this.providerSearchForm.errors);
      this.searchState.setShowSpinner(false);
    }
  }

  onQuickSearch(option: any) {
    console.log('Quick Search Clicked!', option);
    this.searchState.setShowSpinner(true);

    var cityOrZip = this.providerSearchForm.get('cityOrZip')?.value ?? null;

    if(!cityOrZip) {
      console.warn('City or Zip is required for Quick Search.');
      this.searchState.setShowSpinner(false);
      return;
    }

    // You can add further processing logic here
    var request = new PostGetProviderSearchRequest();
    request.specialties = option.specialtyList;
    request.quickSearch = option.quickSearch;
    request.cityOrZip = cityOrZip;
    
    if(this.patientAffiliation != null && this.patientAffiliation != '') {
      request.affiliations = this.patientAffiliation;
    }
    
    this.searchState.setProviderSearchQuery({
      nameOrSpecialty: '' ,
      cityOrZip: cityOrZip,
    });

    if(this.currentLocation != null) {
      request.latitude = this.currentLocation?.latitude;
      request.longitude = this.currentLocation?.longitude;
    }

    sessionStorage.setItem('providerSearchMethod', 'quickSearch');
    console.log('Submitting Quick Search! ',request );
    this.postGetProviderSearch(request);
  }

  postGetProviderSearch(request: PostGetProviderSearchRequest) {
    this.mcAppGateway.PostGetProviderSearch(request).subscribe({
      next: (response) => {
        console.log('Provider Search Response:', response);
        this.providerSearchForm.reset();

        var sortedResponse = this.searchState.sortProviderSearchResults(response || [], this.sortMethod);
        this.searchState.setProviderSearchResults(sortedResponse);
        this.searchState.setProviderSearchFilterToggle(true); 

        if (request.nameOrSpecialty) {
          this.providerSearchFilters?.specialtyList?.forEach(specialty => {
            if (request.nameOrSpecialty?.toLowerCase() === specialty.value.toLowerCase()) {   
              specialty.isSelected = true;
            } 
          });
        }

        if (request.specialties) {
          this.providerSearchFilters?.specialtyList?.forEach(specialty => {
            if (request.specialties?.toLowerCase().includes(specialty.value.toLowerCase())) {
              specialty.isSelected = true;
            }
          });
        }

        if (request.affiliations) {
          this.providerSearchFilters?.affiliationList?.forEach(affiliation => {
            if(request.affiliations?.toLowerCase().includes(affiliation.value.toLowerCase())) {
              affiliation.isSelected = true;
            }
          });
        }

        if(request.cityOrZip != null && request.cityOrZip != '' && this.providerSearchFilters != null) {
          this.providerSearchFilters.location = request.cityOrZip;
        }

        // set the search query in state service for display purposes
        this.searchState.setProviderSearchQuery({
          nameOrSpecialty: request.nameOrSpecialty ?? '',
          cityOrZip: request.cityOrZip ?? '',
        });
        
        this.searchState.setShowSpinner(false);
      },
      error: (error) => {

        // clear previous query & results
        this.searchState.setProviderSearchQuery({nameOrSpecialty: '', cityOrZip: ''});
        this.searchState.clearProviderSearchResults();

        console.error('An error occurred while searching for providers: ', error);
        alert('An error occurred while searching for providers: ' + error.message);

        this.searchState.setShowSpinner(false);
      },
    });
  }
}